import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart, useSite, useAuth } from "../../lib/store";

const NAV = [
  ["/kunst", "Kunst"], ["/prints", "Prints"], ["/kuenstler", "Künstler"], ["/lokale", "Lokale"],
  ["/fundstuecke", "Fundstücke"], ["/einrichtung", "Einrichtung"], ["/vernissagen", "Vernissagen"], ["/ueber-uns", "Über uns"],
];

function Header() {
  const { cart } = useCart();
  const site = useSite();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    setOpen(false);
    const src = new URLSearchParams(loc.search).get("src");
    if (src === "artist") sessionStorage.setItem("kf_src", "artist_link");
  }, [loc.pathname, loc.search]);
  const count = cart.reduce((s, x) => s + x.qty, 0);
  return (
    <header className="sticky top-0 z-40 bg-[#F9D6D2]/90 backdrop-blur-md border-b-2 border-[#121212]" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-16 flex items-center gap-6">
        <Link to="/" className="font-display font-black uppercase text-xl sm:text-2xl tracking-tight leading-none shrink-0" data-testid="logo-link">
          {site?.logo_url ? <img src={site.logo_url} alt={site?.site_name} className="h-9" /> : <>Kunst <span className="text-[#0047FF]">&amp;</span> Fund</>}
        </Link>
        <nav className="hidden xl:flex items-center gap-5 text-sm font-semibold" aria-label="Hauptnavigation">
          {NAV.map(([to, l]) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`} className={({ isActive }) => `link-u py-1 ${isActive ? "text-[#0047FF]" : ""}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <Link to={user ? "/admin" : "/login"} className="h-11 w-11 grid place-items-center rounded-full hover:bg-[#F4EFEA]" aria-label={user ? "Verwaltung" : "Anmelden"} data-testid="header-account-link"><User size={20} /></Link>
          <Link to="/warenkorb" className="relative h-11 w-11 grid place-items-center rounded-full hover:bg-[#F4EFEA]" aria-label={`Warenkorb, ${count} Artikel`} data-testid="header-cart-link">
            <ShoppingBag size={20} />
            {count > 0 && <span className="absolute top-1 right-1 min-w-5 h-5 px-1 rounded-full bg-[#FF5A5F] text-[11px] font-bold grid place-items-center" data-testid="cart-count">{count}</span>}
          </Link>
          <button className="xl:hidden h-11 w-11 grid place-items-center" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Menü" data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <nav className="xl:hidden border-t border-[#121212] bg-[#F9D6D2] px-4 py-4 grid grid-cols-2 gap-1" aria-label="Mobile Navigation" data-testid="mobile-nav">
          {NAV.map(([to, l]) => <NavLink key={to} to={to} className="py-3 px-2 font-semibold text-lg" data-testid={`mnav-${to.slice(1)}`}>{l}</NavLink>)}
          <NavLink to="/kontakt" className="py-3 px-2 font-semibold text-lg">Kontakt</NavLink>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const site = useSite();
  const links = [["/kontakt", "Kontakt"], ["/bewerbung/kuenstler", "Als Künstler:in bewerben"], ["/bewerbung/lokal", "Als Lokal mitmachen"],
    ["/rechtliches/impressum", "Impressum"], ["/rechtliches/datenschutz", "Datenschutz"], ["/rechtliches/versand", "Versand & Abholung"],
    ["/rechtliches/rueckgabe", "Rückgabe"], ["/rechtliches/agb", "Geschäftsbedingungen"]];
  return (
    <footer className="bg-[#121212] text-[#FAF8F5] mt-24" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-16 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <p className="font-display font-black text-4xl sm:text-5xl leading-none">Kunst<br /><span className="text-[#FF5A5F]">&</span> Fund</p>
          <p className="mt-4 text-sm text-[#cfcac2] max-w-sm">Verteilte Galerie: Kunst an den Wänden von Lokalen, Prints, Originale, restaurierte Fundstücke und Einrichtung.</p>
          {site?.checkout?.mode === "test" && <p className="mt-4 inline-block text-xs font-mono bg-[#FFC72C] text-[#121212] px-2 py-1" data-testid="footer-testmode">TESTMODUS · keine echten Zahlungen</p>}
        </div>
        <ul className="space-y-2 text-sm">{links.slice(0, 3).map(([to, l]) => <li key={to}><Link to={to} className="link-u">{l}</Link></li>)}</ul>
        <ul className="space-y-2 text-sm">{links.slice(3).map(([to, l]) => <li key={to}><Link to={to} className="link-u" data-testid={`footer-${to.split("/").pop()}`}>{l}</Link></li>)}</ul>
      </div>
      <div className="border-t border-white/10 text-xs text-[#9b968e] px-4 sm:px-8 py-5 max-w-[1400px] mx-auto">Verkauf über: {site?.seller?.name || "–"}</div>
    </footer>
  );
}

export default function SiteLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-[#FFC72C] px-3 py-2 z-50">Zum Inhalt</a>
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
